import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock3, UserRound, Leaf, Star, ArrowRight, ArrowLeft, Minus, Plus,
  ShieldCheck, BadgeCheck, CheckCircle2, XCircle, Check,
} from "lucide-react";
import { KITCHEN_ROOM_CLEANING_BASE, KITCHEN_ROOM_CLEANING_ADDONS } from "../../../config/pricing";
import { useCityPrice } from "../../../hooks/useCityPrice";

import utils_cleaning_img    from "../../images/stage_01/utils_cleaning.png";
import stove_cleaning_img    from "../../images/stage_01/stove_cleaning.png";
import slab_cleaning_img     from "../../images/stage_01/slab_cleaning.png";
import chimney_cleaning_img  from "../../images/stage_01/chimney_cleaning.png";
import mopping_cleaning_img  from "../../images/stage_01/mopping_cleaning.png";
import dust_wipe_img         from "../../images/stage_01/sweeping,dusting,mopping,wiping.png";
import cooking_prep_img      from "../../images/stage_01/cooking preparation.png";
import cabinet_cleaning_img  from "../../images/stage_01/cabinet_cleaning.png";
import refrigerator_cleaning_img from "../../images/stage_01/refrigetor_cleaning.png";
import microwave_oven_img    from "../../images/stage_01/refrigarator,chimney,microwave cleaning.png";
import window_fan_switch_img from "../../images/stage_01/window,fan& switch board cleaning.png";

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE_URL  = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const CART_KEY       = "cartItems";
const PAGE_PREFIX    = "krc-"; // prefix for all items on this page
const BLOCK_MINUTES  = 30;
const MAX_MINUTES    = 240; // 4 hours — total booking cap (base + add-ons)

const STEPS = [
  { key: "main",   label: "Kitchen Cleaning", sub: "Choose your cleaning duration" },
  { key: "addons", label: "Add-on Services",  sub: "Enhance your cleaning"          },
];

const fmtDuration = (mins) => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

// ─────────────────────────────────────────────────────────────────────────────
// AUTH HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const getLoggedInUser = () => {
  try { return JSON.parse(localStorage.getItem("currentUser") || "null"); }
  catch { return null; }
};

const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

const isLoggedIn = () => !!getLoggedInUser();
const getUserMobile = () => {
  const u = getLoggedInUser();
  return u?.mobile || u?.phone || u?.mobile_number || "";
};

// POST /api/user/user-cart/<mobile>/add/   body: { items: [...] }  — fire-and-forget
const apiCartAdd = async (mobile, items) => {
  if (!mobile || !items?.length) return;
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/add/`,
      { method: "POST", credentials: "include", headers: authHeaders(),
        body: JSON.stringify({ items }) }
    );
    if (!res.ok) console.warn("[Cart] add failed:", res.status);
  } catch (e) { console.warn("[Cart] add error:", e.message); }
};

const readLocalCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); }
  catch { return []; }
};

// ─────────────────────────────────────────────────────────────────────────────
// STATIC DATA
// ─────────────────────────────────────────────────────────────────────────────
const mainService = {
  title: "Kitchen Room Cleaning",
  badge: "Most Booked",
  description: "Complete deep cleaning for a hygienic and sparkling kitchen",
  image: "https://images.unsplash.com/photo-1556911220-bff31c812dba?auto=format&fit=crop&w=1200&q=80",
  meta: [
    { icon: Clock3,    title: "Up to 4 Hours", subtitle: "Max Duration",          color: "#f2b94b" },
    { icon: UserRound, title: "1 Cleaner",     subtitle: "Staff",                 color: "#6d5efc" },
    { icon: Leaf,      title: "100% Safe",     subtitle: "Eco-friendly Products", color: "#4dbb72" },
    { icon: Star,      title: "4.7 (2.1k)",    subtitle: "Ratings",               color: "#f4b400" },
  ],
};

// Always included in the base Kitchen Cleaning package
const includedServices = [
  { name: "Utensils Cleaning", image: utils_cleaning_img,
    points: ["Manual washing of regular utensils.", "Removal of surface oil and food stains.", "Neat drying and stacking after wash."] },
  { name: "Stove Cleaning", image: stove_cleaning_img,
    points: ["Stove top and burner exterior cleaning.", "Oil and grease removal from accessible parts.", "Wiping and shine finish after cleaning."] },
  { name: "Slab Cleaning", image: slab_cleaning_img,
    points: ["Kitchen countertop surface cleaning.", "Removal of surface grease and food spills.", "Sanitizing accessible slab area."] },
  { name: "Chimney Cleaning", image: chimney_cleaning_img,
    points: ["Exterior body cleaning of chimney.", "Mesh/filter cleaning for accessible parts.", "Basic shine-up after cleaning."] },
];

const notIncluded = [
  "Gas pipeline, regulator or electrical repair",
  "Permanent stain removal or stone polishing guarantee",
  "Replacement of appliance parts or spares",
  "Heavy construction residue removal",
];

// Toggle add-ons — each one adds 30 minutes to the booking
const addOnServices = [
  { id: "krc-addon-sweep-mop", name: "Sweeping & Mopping", short: "Sweeping and wet mopping of the kitchen floor", image: mopping_cleaning_img,
    price: KITCHEN_ROOM_CLEANING_ADDONS["krc-addon-sweep-mop"] },
  { id: "krc-addon-dust-wipe", name: "Dusting & Wiping", short: "Dusting shelves, counters and wiping visible surfaces", image: dust_wipe_img,
    price: KITCHEN_ROOM_CLEANING_ADDONS["krc-addon-dust-wipe"] },
  { id: "krc-addon-food-prep", name: "Food Preparation", short: "Washing, cutting and prepping vegetables/ingredients", image: cooking_prep_img,
    price: KITCHEN_ROOM_CLEANING_ADDONS["krc-addon-food-prep"] },
  { id: "krc-addon-cabinet", name: "Kitchen Cabinet Cleaning", short: "Outer surface and shelf cleaning of cabinets", image: cabinet_cleaning_img,
    price: KITCHEN_ROOM_CLEANING_ADDONS["krc-addon-cabinet"] },
  { id: "krc-addon-fridge", name: "Refrigerator Cleaning", short: "Deep cleaning of refrigerator interior and exterior", image: refrigerator_cleaning_img,
    price: KITCHEN_ROOM_CLEANING_ADDONS["krc-addon-fridge"] },
  { id: "krc-addon-microwave", name: "Microwave/Oven Cleaning", short: "Interior and exterior microwave/oven cleaning", image: microwave_oven_img,
    price: KITCHEN_ROOM_CLEANING_ADDONS["krc-addon-microwave"] },
  { id: "krc-addon-window-fan-switch", name: "Window, Fan & Switchboard Cleaning", short: "Kitchen window glass, fan blades and switchboard cleaning", image: window_fan_switch_img,
    price: KITCHEN_ROOM_CLEANING_ADDONS["krc-addon-window-fan-switch"] },
];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const KitchenRoomCleaning = () => {
  const navigate = useNavigate();
  const { cityPrice } = useCityPrice();

  const [step, setStep] = useState("main");
  const stepIndex = STEPS.findIndex((s) => s.key === step);

  const [baseBlocks, setBaseBlocks] = useState(1); // main service duration, in 30-min blocks
  const [addonBlocks, setAddonBlocks] = useState({}); // { [addonId]: 30-min block count }
  const [toast, setToast] = useState("");

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  }, []);

  const getAddonBlocks = useCallback((id) => addonBlocks[id] || 0, [addonBlocks]);
  const selectedAddons = useMemo(
    () => addOnServices.filter((a) => getAddonBlocks(a.id) > 0),
    [getAddonBlocks]
  );

  const baseMinutes  = baseBlocks * BLOCK_MINUTES;
  const addonMinutes = Object.values(addonBlocks).reduce((sum, b) => sum + (b || 0), 0) * BLOCK_MINUTES;
  const totalMinutes = baseMinutes + addonMinutes;

  const priceForDuration = useCallback((minutes) => {
    const first = cityPrice("krc-main-block1", KITCHEN_ROOM_CLEANING_BASE.price);
    const extra = cityPrice("krc-main-block", KITCHEN_ROOM_CLEANING_BASE.perBlockPrice);
    const blocks = minutes / BLOCK_MINUTES;
    return first + (blocks - 1) * extra;
  }, [cityPrice]);

  const basePrice = useMemo(() => priceForDuration(baseMinutes), [priceForDuration, baseMinutes]);

  const addonsPrice = useMemo(
    () => addOnServices.reduce((sum, a) => sum + getAddonBlocks(a.id) * cityPrice(a.id, a.price), 0),
    [getAddonBlocks, cityPrice]
  );

  const totalPrice = basePrice + addonsPrice;

  const handleIncreaseBase = useCallback(() => {
    if (totalMinutes + BLOCK_MINUTES > MAX_MINUTES) {
      showToast("Maximum booking duration is 4 hours");
      return;
    }
    setBaseBlocks((b) => b + 1);
  }, [totalMinutes, showToast]);

  const handleDecreaseBase = useCallback(() => {
    setBaseBlocks((b) => Math.max(1, b - 1));
  }, []);

  const handleIncreaseAddon = useCallback((id) => {
    if (totalMinutes + BLOCK_MINUTES > MAX_MINUTES) {
      showToast("Maximum booking duration is 4 hours");
      return;
    }
    setAddonBlocks((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  }, [totalMinutes, showToast]);

  const handleDecreaseAddon = useCallback((id) => {
    setAddonBlocks((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) - 1) }));
  }, []);

  const goNext = useCallback(() => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].key);
  }, [step]);

  const goBack = useCallback(() => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (idx > 0) setStep(STEPS[idx - 1].key); else navigate(-1);
  }, [step, navigate]);

  // ── Final submit — build cart entries and go to checkout ──────────────────
  const handleCheckout = useCallback(async () => {
    const cartItems = [
      {
        id: "krc-main",
        title: mainService.title,
        name: mainService.title,
        short: `${includedServices.map((s) => s.name).join(", ")} — ${fmtDuration(baseMinutes)}`,
        subtitle: fmtDuration(baseMinutes),
        price: basePrice,
        image: mainService.image,
        category: "Kitchen Room Cleaning",
        qty: 1,
      },
      ...selectedAddons.map((addon) => {
        const blocks = getAddonBlocks(addon.id);
        return {
          id: addon.id,
          title: addon.name,
          name: addon.name,
          short: `${addon.short} — ${fmtDuration(blocks * BLOCK_MINUTES)}`,
          subtitle: fmtDuration(blocks * BLOCK_MINUTES),
          price: cityPrice(addon.id, addon.price),
          image: addon.image,
          category: "Kitchen Room Cleaning",
          qty: blocks,
        };
      }),
    ];

    const others = readLocalCart().filter((i) => !String(i.id).startsWith(PAGE_PREFIX));
    const merged = [...others, ...cartItems];
    localStorage.setItem(CART_KEY, JSON.stringify(merged));
    window.dispatchEvent(new Event("cart-updated"));

    if (isLoggedIn()) {
      await apiCartAdd(getUserMobile(), cartItems);
      navigate("/checkout-instant/summary", { state: { cartItems: merged } });
    } else {
      navigate("/user/user-login", {
        state: { redirectTo: "/checkout-instant/summary", cartItems: merged },
      });
    }
  }, [baseMinutes, basePrice, selectedAddons, getAddonBlocks, cityPrice, navigate]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>

      {toast && <div style={S.toast}>{toast}</div>}

      <div style={S.container}>
        <div style={S.breadcrumb}>
          Home &gt; Home Services &gt; Cleaning &gt; Kitchen Deep Cleaning
        </div>

        {/* ── Hero ── */}
        <section style={S.heroCard}>
          <img src={mainService.image} alt={mainService.title} style={S.heroImage} />
          <div style={S.heroContent}>
            <div style={S.heroTitleRow}>
              <h1 style={S.heroTitle}>{mainService.title}</h1>
              <span style={S.badge}>{mainService.badge}</span>
            </div>
            <p style={S.heroText}>{mainService.description}</p>
            <div style={S.metaRow}>
              {mainService.meta.map((m) => {
                const Icon = m.icon;
                return (
                  <div key={m.title} style={S.metaItem}>
                    <div style={{ ...S.metaIcon, color: m.color }}><Icon size={16} /></div>
                    <div>
                      <div style={S.metaTitle}>{m.title}</div>
                      <div style={S.metaSub}>{m.subtitle}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Trust badges ── */}
        <div style={S.trustRow}>
          {[
            { icon: <BadgeCheck size={16} color="#6d5efc" />, label: "Verified Professionals" },
            { icon: <Leaf       size={16} color="#22c55e" />, label: "Eco-friendly Products"  },
            { icon: <ShieldCheck size={16} color="#6366f1" />, label: "Satisfaction Guarantee" },
          ].map((t) => (
            <div key={t.label} style={S.trustItem}>
              {t.icon}
              <span style={S.trustLabel}>{t.label}</span>
            </div>
          ))}
        </div>

        {/* ── Step progress ── */}
        <div style={S.stepRowOuter}>
          <div style={S.stepRow}>
            {STEPS.map((s, i) => (
              <React.Fragment key={s.key}>
                <div style={S.stepItem}>
                  <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                    {i < stepIndex ? <CheckCircle2 size={13} /> : i + 1}
                  </div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                </div>
                {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
              </React.Fragment>
            ))}
          </div>
          <div style={S.stepSub}>{STEPS[stepIndex]?.sub}</div>
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* ── Step 1: Kitchen Cleaning (included + duration) ─────────────── */}
        {step === "main" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>What's Included</h2>
            <p style={S.sectionSub}>Every Kitchen Cleaning booking always includes these services</p>

            <div style={S.includedScrollRow}>
              {includedServices.map((item) => (
                <div key={item.name} style={S.includedScrollCard}>
                  <img src={item.image} alt={item.name} style={S.includedImg} />
                  <div style={S.includedBody}>
                    <div style={S.includedTitle}>{item.name}</div>
                    <ul style={S.includedList}>
                      {item.points.map((pt) => (
                        <li key={pt} style={S.includedListItem}>
                          <CheckCircle2 size={13} color="#22c55e" /><span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div style={S.notIncludedBox}>
              <div style={S.notIncludedTitle}>Not Included</div>
              <ul style={S.notIncludedList}>
                {notIncluded.map((pt) => (
                  <li key={pt} style={S.notIncludedItem}>
                    <XCircle size={14} color="#ef4444" /><span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>

            <h2 style={{ ...S.sectionTitle, marginTop: "24px" }}>Choose Duration</h2>
            <p style={S.sectionSub}>Increase in 30-minute steps, up to a maximum of 4 hours</p>

            <div style={S.durationRow}>
              <div>
                <div style={S.durationLabel}>Kitchen Cleaning Duration</div>
                <div style={S.durationValue}>{fmtDuration(baseMinutes)}</div>
              </div>
              <div style={S.qtyRow}>
                <button type="button" style={S.qtyBtn} onClick={handleDecreaseBase} disabled={baseBlocks <= 1}>
                  <Minus size={14} />
                </button>
                <span style={S.qtyText}>{fmtDuration(baseMinutes)}</span>
                <button type="button" style={S.qtyBtnP} onClick={handleIncreaseBase} disabled={totalMinutes >= MAX_MINUTES}>
                  <Plus size={14} />
                </button>
              </div>
            </div>
            <div style={S.basePrice}>₹{basePrice}</div>

            <button type="button" style={S.continueBtn} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 2: Add-on Services ─────────────────────────────────────── */}
        {step === "addons" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Add-on Services</h2>
            <p style={S.sectionSub}>Each +30 min step adds to your booking — total stays capped at 4 hours</p>

            <div style={S.meterBox}>
              <div style={S.meterLabelRow}>
                <span>Total Duration</span>
                <span style={{ fontWeight: 800 }}>{fmtDuration(totalMinutes)} / 4 hr</span>
              </div>
              <div style={S.meterTrack}>
                <div style={{ ...S.meterFill, width: `${(totalMinutes / MAX_MINUTES) * 100}%` }} />
              </div>
            </div>

            <div style={S.addonGrid}>
              {addOnServices.map((addon) => {
                const blocks = getAddonBlocks(addon.id);
                const selected = blocks > 0;
                const canIncrease = totalMinutes + BLOCK_MINUTES <= MAX_MINUTES;
                return (
                  <div key={addon.id} style={{ ...S.addonCard, ...(selected ? S.addonCardActive : {}) }}>
                    <div style={S.addonImgWrap}>
                      <img src={addon.image} alt={addon.name} style={S.addonImg} />
                      <div style={S.addonPriceBadge}>₹{cityPrice(addon.id, addon.price)} · +30 min</div>
                      {selected && (
                        <div style={S.addonCheckBadge}><Check size={15} color="#fff" /></div>
                      )}
                    </div>
                    <div style={S.addonBody}>
                      <div style={S.addonTitle}>{addon.name}</div>
                      <div style={S.addonDesc}>{addon.short}</div>
                      <div style={S.addonQtyRow}>
                        <button type="button" style={S.qtyBtn} onClick={() => handleDecreaseAddon(addon.id)} disabled={blocks <= 0}>
                          <Minus size={14} />
                        </button>
                        <span style={S.qtyText}>{blocks > 0 ? fmtDuration(blocks * BLOCK_MINUTES) : "Not added"}</span>
                        <button type="button" style={S.qtyBtnP} onClick={() => handleIncreaseAddon(addon.id)} disabled={!canIncrease}>
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={S.checkoutSummary}>
              <div>
                <div style={S.checkoutSummaryLabel}>
                  {1 + selectedAddons.length} service{selectedAddons.length > 0 ? "s" : ""} · {fmtDuration(totalMinutes)}
                </div>
                <div style={S.checkoutSummarySub}>
                  Kitchen Cleaning{selectedAddons.length > 0 ? ` + ${selectedAddons.length} add-on${selectedAddons.length > 1 ? "s" : ""}` : ""}
                </div>
              </div>
              <div style={S.checkoutSummaryTotal}>₹{totalPrice}</div>
            </div>

            <button type="button" style={S.continueBtn} onClick={handleCheckout}>
              Proceed to Checkout <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:             { background:"#fafbff", minHeight:"100vh", paddingBottom:"60px", fontFamily:"Inter,ui-sans-serif,system-ui,sans-serif", color:"#121826" },
  toast:            { position:"fixed", top:"20px", right:"20px", background:"#111827", color:"#fff", padding:"12px 18px", borderRadius:"12px", zIndex:9999, fontWeight:600, boxShadow:"0 10px 30px rgba(0,0,0,.15)" },
  container:        { maxWidth:"1000px", margin:"0 auto", padding:"24px 18px 0" },
  breadcrumb:       { fontSize:"13px", color:"#7b8195", fontWeight:500, marginBottom:"18px" },

  // Hero
  heroCard:         { background:"#fff", border:"1px solid #eceef5", borderRadius:"20px", padding:"20px", display:"grid", gridTemplateColumns:"300px 1fr", gap:"24px", marginBottom:"20px", boxShadow:"0 4px 20px rgba(0,0,0,0.04)" },
  heroImage:        { width:"100%", aspectRatio:"16 / 9", objectFit:"cover", borderRadius:"14px" },
  heroContent:      { display:"flex", flexDirection:"column", justifyContent:"center" },
  heroTitleRow:     { display:"flex", alignItems:"center", gap:"12px", flexWrap:"wrap", marginBottom:"10px" },
  heroTitle:        { margin:0, fontSize:"30px", fontWeight:800, color:"#111827" },
  badge:            { background:"#e9f9ec", color:"#2f9e44", fontSize:"12px", fontWeight:700, padding:"5px 10px", borderRadius:"999px" },
  heroText:         { margin:"0 0 18px", color:"#6b7280", fontSize:"15px", lineHeight:1.6 },
  metaRow:          { display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:"14px" },
  metaItem:         { display:"flex", alignItems:"center", gap:"10px" },
  metaIcon:         { width:"34px", height:"34px", borderRadius:"50%", background:"#f5f7fb", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  metaTitle:        { fontSize:"14px", fontWeight:700, color:"#111827" },
  metaSub:          { fontSize:"12px", color:"#7b8195" },

  // Trust row
  trustRow:         { display:"flex", gap:"20px", marginBottom:"24px", flexWrap:"wrap" },
  trustItem:        { display:"flex", alignItems:"center", gap:"8px", background:"#fff", border:"1px solid #eceef5", borderRadius:"999px", padding:"8px 14px" },
  trustLabel:       { fontSize:"13px", fontWeight:600, color:"#374151" },

  // Step progress
  stepRowOuter:     { marginBottom: "20px" },
  stepRow:          { display:"flex", alignItems:"center", justifyContent:"center", gap:"8px", overflowX:"auto", padding:"4px 2px", flexWrap:"nowrap" },
  stepItem:         { display:"flex", alignItems:"center", gap:"8px", flexShrink:0 },
  stepCircle:       { width:"28px", height:"28px", borderRadius:"50%", background:"#e5e7eb", color:"#6b7280", fontSize:"12px", fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  stepCircleActive: { background:"#5b5bf6", color:"#fff" },
  stepCircleDone:   { background:"#e5e4ff", color:"#5b5bf6" },
  stepLabel:        { fontSize:"13px", fontWeight:700, color:"#9ca3af", whiteSpace:"nowrap" },
  stepLabelActive:  { color:"#111827" },
  stepSub:          { fontSize:"12px", color:"#9ca3af", textAlign:"center", marginTop:"10px" },
  stepConnector:    { width:"32px", height:"2px", background:"#e5e7eb", flexShrink:0 },
  stepConnectorActive: { background:"#5b5bf6" },
  backBtn:          { display:"inline-flex", alignItems:"center", gap:"6px", background:"none", border:"none", color:"#5b5bf6", fontWeight:700, fontSize:"13px", cursor:"pointer", marginBottom:"16px", padding:0 },

  // Step card
  card:             { background:"#fff", border:"1px solid #eceef5", borderRadius:"20px", padding:"24px", marginBottom:"28px" },
  sectionTitle:     { margin:0, fontSize:"20px", fontWeight:800, color:"#121826", marginBottom:"4px" },
  sectionSub:       { margin:"0 0 16px", fontSize:"13px", color:"#7b8195" },

  // Included — horizontal scroll strip
  includedScrollRow:  { display:"flex", gap:"14px", overflowX:"auto", paddingBottom:"10px", marginBottom:"18px" },
  includedScrollCard: { flex:"0 0 220px", border:"1px solid #eceef5", borderRadius:"14px", overflow:"hidden", display:"flex", flexDirection:"column" },
  includedImg:        { width:"100%", aspectRatio:"1 / 1", objectFit:"cover", display:"block" },
  includedBody:       { padding:"10px 12px" },
  includedTitle:      { fontSize:"13px", fontWeight:700, color:"#111827", marginBottom:"6px" },
  includedList:       { margin:0, padding:0, listStyle:"none", display:"grid", gap:"5px" },
  includedListItem:   { display:"flex", alignItems:"flex-start", gap:"6px", fontSize:"11px", color:"#4b5563", lineHeight:1.4 },

  notIncludedBox:   { background:"#fff7f7", border:"1px solid #fde2e2", borderRadius:"14px", padding:"14px 16px" },
  notIncludedTitle: { fontSize:"13px", fontWeight:700, color:"#b91c1c", marginBottom:"8px" },
  notIncludedList:  { margin:0, padding:0, listStyle:"none", display:"grid", gap:"6px" },
  notIncludedItem:  { display:"flex", alignItems:"flex-start", gap:"7px", fontSize:"12px", color:"#7f1d1d", lineHeight:1.4 },

  // Duration stepper — +30 min increments, up to 4 hours
  durationRow:      { display:"flex", alignItems:"center", justifyContent:"space-between", background:"#f8f9fc", border:"1px solid #eceef5", borderRadius:"14px", padding:"14px 16px", marginBottom:"6px" },
  durationLabel:    { fontSize:"12px", color:"#7b8195", fontWeight:600, marginBottom:"2px" },
  durationValue:    { fontSize:"16px", fontWeight:800, color:"#111827" },
  qtyRow:           { display:"flex", alignItems:"center", gap:"10px" },
  qtyBtn:           { width:"30px", height:"30px", borderRadius:"50%", border:"1px solid #e4e7ef", background:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#6b7280", flexShrink:0 },
  qtyBtnP:          { width:"30px", height:"30px", borderRadius:"50%", border:"none", background:"#5b5bf6", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#fff", flexShrink:0 },
  qtyText:          { minWidth:"56px", textAlign:"center", fontSize:"13px", fontWeight:700, color:"#111827" },
  basePrice:        { fontSize:"22px", fontWeight:800, color:"#111827", margin:"10px 0 22px" },

  continueBtn:      { width:"100%", marginTop:"6px", padding:"15px 0", borderRadius:"12px", background:"linear-gradient(90deg,#5a5bf6,#3f53f5)", color:"#fff", fontSize:"15px", fontWeight:800, border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"8px", boxShadow:"0 6px 18px rgba(91,91,246,0.3)" },

  // Duration meter (add-ons step)
  meterBox:         { marginBottom:"18px" },
  meterLabelRow:    { display:"flex", justifyContent:"space-between", fontSize:"12px", color:"#6b7280", marginBottom:"6px" },
  meterTrack:       { height:"8px", background:"#eceef5", borderRadius:"999px", overflow:"hidden" },
  meterFill:        { height:"100%", background:"linear-gradient(90deg,#5a5bf6,#3f53f5)", borderRadius:"999px", transition:"width .2s" },

  // Add-on cards — effective/e-commerce style grid
  addonGrid:        { display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:"16px", marginBottom:"22px" },
  addonCard:         { position:"relative", textAlign:"left", border:"1.5px solid #e5e7eb", borderRadius:"18px", overflow:"hidden", background:"#fff", transition:"box-shadow .15s, border-color .15s" },
  addonCardActive:   { borderColor:"#5b5bf6", boxShadow:"0 0 0 3px #e5e4ff" },
  addonImgWrap:      { position:"relative" },
  addonImg:          { width:"100%", aspectRatio:"1 / 1", objectFit:"cover", display:"block" },
  addonPriceBadge:   { position:"absolute", bottom:"8px", left:"8px", background:"rgba(17,24,39,0.82)", color:"#fff", fontSize:"11px", fontWeight:700, padding:"4px 10px", borderRadius:"999px" },
  addonCheckBadge:   { position:"absolute", top:"8px", right:"8px", width:"26px", height:"26px", borderRadius:"50%", background:"#5b5bf6", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.25)" },
  addonBody:         { padding:"12px 14px 16px" },
  addonTitle:        { fontSize:"14px", fontWeight:800, color:"#111827", marginBottom:"4px" },
  addonDesc:         { fontSize:"11.5px", color:"#6b7280", lineHeight:1.4, marginBottom:"10px", minHeight:"32px" },
  addonQtyRow:       { display:"flex", alignItems:"center", gap:"10px" },

  checkoutSummary:      { display:"flex", justifyContent:"space-between", alignItems:"center", background:"#f8f9fc", border:"1px solid #eceef5", borderRadius:"14px", padding:"14px 16px", marginBottom:"16px" },
  checkoutSummaryLabel: { fontSize:"14px", fontWeight:700, color:"#111827" },
  checkoutSummarySub:   { fontSize:"12px", color:"#7b8195", marginTop:"2px" },
  checkoutSummaryTotal: { fontSize:"22px", fontWeight:800, color:"#111827" },
};

export default KitchenRoomCleaning;
